import { Fragment, FC, MouseEvent, useContext } from "react";
import { SavingGoal } from "@/generated/graphql";
import { UserContext } from "@/App";
import { formatAmount } from "@/utils/format";
import { AnchorActionDropdownElProps } from "@/hooks/useActionDropdown";
import { MainListItemStyled } from "@/shared";
import { Box } from "@/components/ui/Box";
import { Typography } from "@/components/ui/Typography";
import { CategoryDetailsStyled } from "@/features/categories/categories-list/categories-list-style";
import { SavingGoalItemDetails } from "../../saving-goal-item-details/saving-goal-item-details";
import { ProgressBar } from "@/components/progress-bar/progress-bar";
import { IconMenu } from "@/components/icon-menu/icon-menu";

interface Props {
  savingGoals: SavingGoal[];
  anchorActionDropdownEl: AnchorActionDropdownElProps;
  onEditSavingGoal: (savingGoal: SavingGoal) => void;
  onRemoveSavingGoal: (savingGoalId: string) => void;
  onActionsDropdownClick: (
    event: MouseEvent<HTMLElement>,
    anchorIndex: string,
  ) => void;
  onActionsDropdownClose: (anchorIndex: string) => void;
  calculateSavingGoalData: (savingGoal: SavingGoal) => {
    monthsLeftToSave: number;
    savePerMonth: number;
    savedTillNow: number;
  };
}

export const SavingGoalsListData: FC<Props> = ({
  savingGoals,
  anchorActionDropdownEl,
  onEditSavingGoal,
  onRemoveSavingGoal,
  onActionsDropdownClick,
  onActionsDropdownClose,
  calculateSavingGoalData,
}) => {
  const userCurrency = useContext(UserContext);

  return (
    <>
      {savingGoals.map((savingGoal: SavingGoal) => {
        const savingGoalId = savingGoal.id;
        const { monthsLeftToSave, savePerMonth, savedTillNow } =
          calculateSavingGoalData(savingGoal);

        // Three distinct outcomes, not two. "You have reached your goal date!"
        // fired purely on the date, so a goal that ran out of time while still
        // short was congratulated in the same words as one that was funded.
        const funded = savedTillNow >= savingGoal.goalAmount;
        const overdue = !funded && monthsLeftToSave <= 0;
        const shortfall = savingGoal.goalAmount - savedTillNow;

        return (
          <Fragment key={savingGoalId}>
            <MainListItemStyled>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* No fixed width: a long goal name truncated while the row
                    had hundreds of pixels to spare. */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body1" color="text.primary">
                    {savingGoal.name}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="secondary">
                    {funded && <>Goal reached</>}
                    {overdue && (
                      <>
                        Goal date passed &middot;{" "}
                        {formatAmount(shortfall, userCurrency)} short
                      </>
                    )}
                    {!funded && !overdue && (
                      <>
                        {formatAmount(savePerMonth, userCurrency)}/month,{" "}
                        {monthsLeftToSave} months left
                      </>
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Positive: saving toward a goal is good news, so a funded
                  goal reads as achieved rather than greyed out. */}
              <ProgressBar
                value={savedTillNow}
                maxValue={savingGoal.goalAmount}
                tone="positive"
              />

              <Box sx={{ display: "flex" }}>
                <SavingGoalItemDetails
                  goalAmount={savingGoal.goalAmount}
                  savedTillNow={savedTillNow}
                />
                <CategoryDetailsStyled>
                  <IconMenu
                    itemId={savingGoalId}
                    anchorActionDropdownEl={anchorActionDropdownEl}
                    handleOnEdit={() => onEditSavingGoal(savingGoal)}
                    handleOnRemove={() => onRemoveSavingGoal(savingGoalId)}
                    handleOnOpenMenu={(event: MouseEvent<HTMLElement>) =>
                      onActionsDropdownClick(event, savingGoalId)
                    }
                    handleOnCloseMenu={() =>
                      onActionsDropdownClose(savingGoalId)
                    }
                  />
                </CategoryDetailsStyled>
              </Box>
            </MainListItemStyled>
          </Fragment>
        );
      })}
    </>
  );
};
